import { useAuth0 } from '@auth0/auth0-react';
import { v4 as uuidv4 } from 'uuid';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

import useAppMonitor from '~/lib/hooks/useAppMonitor';
import useHandleLogout from '~/lib/hooks/useHandleLogout';
import { bootstrapErrorKey } from '~/routes/constants';

export type BootstrapQueryError =
  | FetchBaseQueryError
  | SerializedError
  | undefined;

export type BootstrapFailurePayload = {
  correlationId: string;
  endpointName: string;
  rawMessage: string;
};

// Returns the safest human-readable string from an RTK Query error without serializing an arbitrary payload object.
export const extractServerMessage = (err: BootstrapQueryError): string => {
  if (!err) return 'Unknown server error';
  const data = (err as FetchBaseQueryError).data as
    | { message?: unknown }
    | undefined;

  if (data && typeof data.message === 'string' && data.message.length > 0) {
    return data.message;
  }

  const status = (err as FetchBaseQueryError).status;
  if (status !== undefined) return `Server error (${String(status)})`;

  const serialized = (err as SerializedError).message;
  if (typeof serialized === 'string' && serialized.length > 0) {
    return serialized;
  }
  return 'Unknown server error';
};

const useBootstrapFailureRedirect = () => {
  const appMonitor = useAppMonitor();
  const { logout } = useHandleLogout();
  const { user: auth0User } = useAuth0();

  return (err: BootstrapQueryError, endpointName: string) => {
    const correlationId = uuidv4().slice(0, 8);
    const rawMessage = extractServerMessage(err);

    const username = auth0User?.email ?? auth0User?.sub ?? 'unknown';
    const status = (err as FetchBaseQueryError | undefined)?.status;
    const payload: BootstrapFailurePayload = {
      correlationId,
      endpointName,
      rawMessage,
    };

    appMonitor.captureException(
      new Error(
        `Bootstrap failure for username=${username} [correlationId=${correlationId}] [endpoint=${endpointName}]: ${rawMessage}`,
      ),
      {
        correlationId,
        endpointName,
        username,
        status: status !== undefined ? String(status) : undefined,
        rawMessage,
        rawError: err,
      },
    );

    sessionStorage.setItem(bootstrapErrorKey, JSON.stringify(payload));
    logout();
  };
};

export default useBootstrapFailureRedirect;
